import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Animated, Easing, Pressable, PressableProps, StyleProp, StyleSheet, Text, View, ViewStyle } from 'react-native';
import { Check, CircleCheck, Info, Play } from 'lucide-react-native';
import { JellyfinItem } from '../api/jellyfin';
import { Credentials } from '../storage/credentials';
import { colors, motion, radius, spacing , systemSansFont} from '../theme/tokens';
import { FocusablePressable } from './FocusablePressable';
import { CARD_CONTROL_TINT_VERSION, CardControlTints, getCardControlTints } from '../utils/imageAccent';
import { isTelevisionRuntime } from '../utils/platformCapabilities';
import { focusRegisteredNodeFromBack, getFocusModality, subscribeFocusModality } from '../tvNavigation/registry';

type Props = {
    item: JellyfinItem;
    credentials: Credentials;
    imageKind?: 'Primary' | 'Backdrop' | 'Thumb';
    visible: boolean;
    width: number;
    height: number;
    landscape?: boolean;
    showTypeChip?: boolean;
    showRuntime?: boolean;
    standaloneInfo?: boolean;
    controlsOnly?: boolean;
    played?: boolean;
    playedPending?: boolean;
    onPlay: () => void;
    onInfo: () => void;
    onTogglePlayed?: () => void;
    onHoverChange?: (hovered: boolean) => void;
    focusId?: string;
    focusGroup?: string;
    controllerActionMode?: boolean;
    cardFocusDestinations?: import('../tvNavigation/core').FocusDestinations;
};

type ReactiveActionProps = {
    accessibilityLabel: string;
    children: ReactNode;
    containerStyle?: StyleProp<ViewStyle>;
    onIntent: () => void;
    onPress: NonNullable<PressableProps['onPress']>;
    onPressIn?: PressableProps['onPressIn'];
    surfaceStyle: (active: boolean, focused: boolean) => StyleProp<ViewStyle>;
    activeScale?: number;
    focusable: boolean;
    focusId?: string;
    focusGroup?: string;
    focusDestinations?: import('../tvNavigation/core').FocusDestinations;
    onBack?: () => boolean | void;
};

function ReactiveCardAction({ accessibilityLabel, children, containerStyle, focusable, focusId, focusGroup, focusDestinations, onBack, onIntent, onPress, onPressIn, surfaceStyle, activeScale = 1.04 }: ReactiveActionProps) {
    const [hovered, setHovered] = useState(false);
    const [focusVisible, setFocusVisible] = useState(false);
    const [pressed, setPressed] = useState(false);
    const attention = useRef(new Animated.Value(0)).current;
    const compression = useRef(new Animated.Value(0)).current;
    const active = hovered || focusVisible;
    useEffect(() => {
        Animated.timing(attention, {
            toValue: active ? 1 : 0,
            duration: active ? motion.controlEnter : motion.controlExit,
            easing: active ? Easing.out(Easing.cubic) : Easing.inOut(Easing.quad),
            useNativeDriver: true,
        }).start();
    }, [active, attention]);
    useEffect(() => {
        Animated.timing(compression, {
            toValue: pressed ? 1 : 0,
            duration: pressed ? motion.controlPress : motion.controlExit,
            easing: Easing.out(Easing.quad),
            useNativeDriver: true,
        }).start();
    }, [compression, pressed]);
    const scale = Animated.multiply(
        attention.interpolate({ inputRange: [0, 1], outputRange: [1, activeScale] }),
        compression.interpolate({ inputRange: [0, 1], outputRange: [1, 0.965] }),
    );
    const translateY = attention.interpolate({ inputRange: [0, 1], outputRange: [0, -2] });
    return (
        <Animated.View style={[containerStyle, { transform: [{ translateY }, { scale }] }]}>
            <FocusablePressable
                accessibilityRole="button"
                accessibilityLabel={accessibilityLabel}
                focusRole="card-action"
                focusId={focusId}
                focusGroup={focusGroup}
                focusDestinations={focusDestinations}
                onControllerBack={onBack}
                focusHalo={false}
                focusable={focusable}
                onFocus={onIntent}
                onFocusVisibleChange={setFocusVisible}
                onHoverIn={() => { setHovered(true); onIntent(); }}
                onHoverOut={() => setHovered(false)}
                onPressIn={(event) => { setPressed(true); onPressIn?.(event); }}
                onPressOut={() => setPressed(false)}
                onPress={onPress}
                style={() => surfaceStyle(active || pressed, focusVisible)}
            >
                {children}
            </FocusablePressable>
        </Animated.View>
    );
}

export function formatRuntime(ticks?: number) {
    if (!ticks) return '';
    const minutes = Math.round(ticks / 600000000);
    return minutes >= 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes}m`;
}

export function CardHoverOverlay({ item, credentials, imageKind = 'Primary', visible, width, height, landscape = false, showTypeChip = false, showRuntime = true, standaloneInfo = false, controlsOnly = false, played = false, playedPending = false, onPlay, onInfo, onTogglePlayed, onHoverChange, focusId, focusGroup, controllerActionMode = false, cardFocusDestinations }: Props) {
    const television = isTelevisionRuntime();
    const [modality, setModality] = useState(getFocusModality);
    useEffect(() => subscribeFocusModality(setModality), []);
    const directionalTVNavigation = modality !== 'pointer' && (television || modality === 'gamepad' || modality === 'native-tv');
    const effectiveVisible = visible && (!directionalTVNavigation || controllerActionMode);
    const watchedFocusId = focusId ? `${focusId}.watched` : undefined;
    const detailsFocusId = focusId ? `${focusId}.details` : undefined;
    const playFocusId = focusId ? `${focusId}.play` : undefined;
    const closeActionLayer = () => focusId ? focusRegisteredNodeFromBack(focusId) : false;
    const [controlTints, setControlTints] = useState<CardControlTints | null>(null);
    const overlayOpacity = useRef(new Animated.Value(0)).current;
    useEffect(() => {
        Animated.timing(overlayOpacity, {
            toValue: effectiveVisible ? 1 : 0,
            duration: visible ? motion.controlEnter : motion.controlExit,
            easing: Easing.bezier(0.4, 0, 0.2, 1),
            useNativeDriver: true,
        }).start();
    }, [effectiveVisible, overlayOpacity]);
    useEffect(() => {
        if (!effectiveVisible || !item.Id) return undefined;
        let current = true;
        void getCardControlTints(credentials, item, imageKind)
            .then((tints) => { if (current) setControlTints(tints); })
            .catch(() => { if (current) setControlTints(null); });
        return () => { current = false; };
    }, [credentials, effectiveVisible, imageKind, item, item.Id, CARD_CONTROL_TINT_VERSION]);
    const typeChip = item.Type === 'Series' ? 'TV Show' : item.Type ?? '';
    const chips = landscape ? [] : [item.ProductionYear ? String(item.ProductionYear) : '', showTypeChip ? typeChip : ''].filter(Boolean).slice(0, 2);
    const runtime = showRuntime ? formatRuntime(item.RunTimeTicks) : '';
    const controlInset = television ? 14 : width > 250 ? 12 : 8;
    const playButtonSize = television ? 58 : 48;
    const stopAndRun = (callback: () => void) => (event: { stopPropagation?: () => void; preventDefault?: () => void }) => {
        event.stopPropagation?.();
        callback();
    };
    const stopPropagationOnly = (event: { stopPropagation?: () => void; preventDefault?: () => void }) => {
        event.stopPropagation?.();
    };
    const watchedDestinations = { left: detailsFocusId, right: focusId };
    const detailsDestinations = { left: playFocusId, right: watchedFocusId ?? focusId };
    const verticalDestinations = { ...(cardFocusDestinations?.up ? { up: cardFocusDestinations.up } : {}), ...(cardFocusDestinations?.down ? { down: cardFocusDestinations.down } : {}) };
    const playDestinations = { ...verticalDestinations, left: focusId, right: detailsFocusId };
    // Legacy graph shape: const playDestinations = { left: focusId, right: detailsFocusId };
    const watchedTVDestinations = { ...verticalDestinations, ...watchedDestinations };
    const detailsTVDestinations = { ...verticalDestinations, ...detailsDestinations };
    const playTVDestinations = playDestinations;
    const watchedAction = onTogglePlayed ? <ReactiveCardAction accessibilityLabel={`${played ? 'Mark as unplayed' : 'Mark as played'}: ${item.Name ?? 'title'}`} focusId={watchedFocusId} focusGroup={focusGroup} focusDestinations={watchedTVDestinations} onBack={closeActionLayer} focusable={effectiveVisible && !playedPending} onPressIn={stopPropagationOnly} onIntent={() => onHoverChange?.(true)} onPress={stopAndRun(onTogglePlayed)} surfaceStyle={(active) => [styles.cornerAction, television && styles.cornerActionTelevision, played && styles.cornerActionPlayed, active && styles.cornerActionActive, playedPending && styles.cornerActionPending, controlTints && { backgroundColor: active ? controlTints.checkActive : controlTints.check }]}> 
        {played ? <CircleCheck color={colors.text} size={television ? 18 : 14} strokeWidth={2.3} /> : <Check color={colors.text} size={television ? 18 : 14} strokeWidth={2.5} />}
    </ReactiveCardAction> : null;
    const detailsAction = !controlsOnly ? <ReactiveCardAction accessibilityLabel={`Media info for ${item.Name ?? 'title'}`} focusId={detailsFocusId} focusGroup={focusGroup} focusDestinations={detailsTVDestinations} onBack={closeActionLayer} focusable={effectiveVisible} onPressIn={stopPropagationOnly} onIntent={() => onHoverChange?.(true)} onPress={stopAndRun(onInfo)} surfaceStyle={(active) => [styles.cornerAction, television && styles.cornerActionTelevision, controlTints && { backgroundColor: active ? controlTints.infoActive : controlTints.info }]}> 
        <Info color={colors.text} size={television ? 17 : 13} strokeWidth={2.2} />
    </ReactiveCardAction> : null;
    return (
        <Animated.View testID={effectiveVisible ? 'card-hover-overlay' : undefined} pointerEvents={effectiveVisible ? (controlsOnly ? 'box-none' : 'auto') : 'none'} style={[styles.root, { opacity: overlayOpacity }]}>
            {!controlsOnly ? <Pressable
                accessible={false}
                focusable={false}
                onHoverIn={() => onHoverChange?.(true)}
                onPress={stopAndRun(onInfo)}
                style={styles.hoverSurface}
            >
            {chips.length ? <View pointerEvents="none" style={styles.chips}>{chips.map((chip) => <View key={chip} style={[styles.chip, television && styles.chipTelevision]}><Text style={[styles.chipText, television && styles.chipTextTelevision]}>{chip}</Text></View>)}</View> : null}
            {runtime ? <Text style={[styles.runtime, television && styles.runtimeTelevision, landscape ? styles.runtimeLandscape : styles.runtimePortrait]}>{landscape ? `${runtime} total` : runtime}</Text> : null}
            </Pressable> : null}
            <View pointerEvents="box-none" style={[styles.cornerActions, television && styles.cornerActionsTelevision, (controlsOnly || !onTogglePlayed) && styles.cornerActionsSingle, television && (controlsOnly || !onTogglePlayed) && styles.cornerActionsSingleTelevision, standaloneInfo && styles.cornerActionsStandalone, television && standaloneInfo && styles.cornerActionsStandaloneTelevision, { top: controlInset, right: controlInset }]}>
                {television ? <>{detailsAction}{watchedAction}</> : <>{watchedAction}{detailsAction}</>}
            </View>
            {!controlsOnly && onPlay ? <ReactiveCardAction accessibilityLabel={`Play ${item.Name ?? 'title'}`} focusId={playFocusId} focusGroup={focusGroup} focusDestinations={playDestinations} onBack={closeActionLayer} containerStyle={[styles.playButtonPosition, { left: Math.round((width - playButtonSize) / 2), top: Math.round((height - playButtonSize) / 2) }]} activeScale={1.06} focusable={effectiveVisible} onPressIn={stopPropagationOnly} onIntent={() => onHoverChange?.(true)} onPress={stopAndRun(onPlay)} surfaceStyle={(active) => [styles.playButton, television && styles.playButtonTelevision, controlTints && { backgroundColor: active ? controlTints.playActive : controlTints.play, borderColor: controlTints.playActive }]}>
                <Play color={colors.text} fill={colors.text} size={television ? 27 : landscape ? 22 : 20} />
            </ReactiveCardAction> : null}
        </Animated.View>
    );
}

const styles = StyleSheet.create({
    root: { ...StyleSheet.absoluteFillObject, borderRadius: radius.medium, overflow: 'hidden', zIndex: 3 },
    hoverSurface: { ...StyleSheet.absoluteFillObject },
    chips: { position: 'absolute', top: spacing.sm, left: spacing.sm, right: 48, flexDirection: 'row', flexWrap: 'wrap', gap: 5 },
    chip: { borderRadius: 3, paddingHorizontal: 6, paddingVertical: 3, backgroundColor: 'rgba(0,0,0,0.58)', borderWidth: StyleSheet.hairlineWidth, borderColor: 'rgba(255,255,255,0.18)' },
    chipTelevision: { borderRadius: 5, paddingHorizontal: 8, paddingVertical: 5, borderWidth: 1 },
    chipText: { fontFamily: systemSansFont,  color: 'rgba(255,255,255,0.9)', fontSize: 9, lineHeight: 11, fontWeight: '800' },
    chipTextTelevision: { fontSize: 12, lineHeight: 15 },
    cornerActions: { position: 'absolute', width: 58, height: 30, padding: 2, borderRadius: 9, flexDirection: 'row', alignItems: 'center', gap: 2, backgroundColor: 'rgba(8,8,8,0.64)', borderWidth: StyleSheet.hairlineWidth, borderColor: 'rgba(255,255,255,0.18)', shadowColor: '#000', shadowOpacity: 0.36, shadowRadius: 10, elevation: 5 },
    cornerActionsTelevision: { width: 70, height: 36, borderRadius: 11 },
    cornerActionsSingle: { width: 30 },
    cornerActionsSingleTelevision: { width: 36 },
    cornerActionsStandalone: { width: 26, height: 26, padding: 0, borderWidth: 0, backgroundColor: 'transparent', shadowOpacity: 0, elevation: 0 },
    cornerActionsStandaloneTelevision: { width: 32, height: 32 },
    cornerAction: { width: 26, height: 26, borderRadius: 7, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(8,8,8,0.68)' },
    cornerActionTelevision: { width: 32, height: 32, borderRadius: 9 },
    cornerActionActive: { shadowOpacity: 0.48, shadowRadius: 12 },
    cornerActionPlayed: { borderColor: 'rgba(255,255,255,0.42)' },
    cornerActionPending: { opacity: 0.55 },
    playButtonPosition: { position: 'absolute' },
    playButton: { width: 48, height: 48, borderRadius: 24, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(10,10,10,0.80)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.28)', shadowColor: '#000', shadowOpacity: 0.42, shadowRadius: 12, elevation: 5 },
    playButtonTelevision: { width: 58, height: 58, borderRadius: 29 },
    playButtonActive: { shadowOpacity: 0.54, shadowRadius: 15 },
    actionFocused: { borderWidth: 2, borderColor: colors.brandBright, shadowColor: colors.brand, shadowOpacity: 0.62, shadowRadius: 10, elevation: 8 },
    runtime: { fontFamily: systemSansFont,  position: 'absolute', bottom: spacing.sm, color: 'rgba(255,255,255,0.76)', fontSize: 10, lineHeight: 14, fontWeight: '700' },
    runtimeTelevision: { bottom: 12, fontSize: 13, lineHeight: 17 },
    runtimePortrait: { left: spacing.sm },
    runtimeLandscape: { right: spacing.sm, paddingHorizontal: 6, borderWidth: StyleSheet.hairlineWidth, borderColor: 'rgba(255,255,255,0.18)', borderRadius: 4, backgroundColor: 'rgba(8,8,8,0.68)', fontWeight: '500' },
});
